import { useState, useEffect, useCallback } from "react";
import { createClient } from "@supabase/supabase-js";
import { subscriptionStyle, LOGO_SRC, fmt } from "./sharedStyle";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

function getToken() {
  return new URLSearchParams(window.location.search).get("token") || "";
}

export default function ManagePage() {
  const token = getToken();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [cycle, setCycle] = useState(null);
  const [products, setProducts] = useState([]);
  const [extrasQty, setExtrasQty] = useState({});
  const [coverFee, setCoverFee] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null); // { type: 'confirmed' | 'skipped', ... }

  const loadCycle = useCallback(() => {
    if (!token) {
      setLoadError("This link is missing its token.");
      setLoading(false);
      return;
    }

    setLoading(true);
    Promise.all([
      fetch(`/api/subscription-manage?token=${encodeURIComponent(token)}`).then((r) => r.json().then((data) => ({ ok: r.ok, data }))),
      supabase.from("products").select("*").eq("visible", true).order("sort_order"),
    ])
      .then(([cycleRes, productsRes]) => {
        if (!cycleRes.ok) {
          setLoadError(cycleRes.data.error || "That link isn't valid.");
        } else {
          setCycle(cycleRes.data);
        }
        setProducts(productsRes.data || []);
      })
      .catch(() => setLoadError("Couldn't load your weekly basket. Please refresh to try again."))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    loadCycle();
  }, [loadCycle]);

  const extrasList = Object.entries(extrasQty)
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => {
      const p = products.find((p) => String(p.id) === id);
      return p ? { name: p.name, quantity: qty, price: p.price } : null;
    })
    .filter(Boolean);

  const setQty = (id, qty) => {
    setExtrasQty((prev) => {
      const next = { ...prev };
      const key = String(id);
      if (qty <= 0) delete next[key];
      else next[key] = qty;
      return next;
    });
  };

  const baseTotal = (cycle?.baseItems || []).reduce((sum, i) => sum + i.price * i.quantity, 0);
  const extrasTotal = extrasList.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const subtotal = baseTotal + extrasTotal;
  const processingFee = parseFloat((subtotal * 0.026 + 0.1).toFixed(2));
  const willCoverFee = cycle?.alwaysCoversFee || coverFee;
  const chargeTotal = willCoverFee ? subtotal + processingFee : subtotal;

  const handleConfirm = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch("/api/subscription-manage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, action: "confirm", extras: extrasList, coverFee }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong.");
      if (!data.success) {
        setSubmitError(data.error || "Your card was declined.");
        return;
      }
      setResult({ type: "confirmed" });
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (!window.confirm("Skip this week's order? You won't be charged and nothing will be prepared.")) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch("/api/subscription-manage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, action: "skip" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong.");
      setResult({ type: "skipped" });
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReactivate = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch("/api/subscription-manage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, action: "reactivate" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong.");
      loadCycle();
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="gg-app">
      <style>{subscriptionStyle}</style>
      <header className="gg-header">
        <div className="gg-logo"><img src={LOGO_SRC} alt="Grind and Gather" /></div>
        <a href="mailto:grindgather@gmail.com" className="gg-contact-link">Get in Touch</a>
      </header>

      <div className="gg-page">
        {loading && <p className="gg-loading">Loading your weekly basket…</p>}

        {!loading && loadError && (
          <>
            <h1>Manage Your Weekly Basket</h1>
            <div className="gg-error">{loadError}</div>
          </>
        )}

        {!loading && !loadError && cycle && cycle.status !== "pending" && !result && (
          <>
            <h1>Already Handled</h1>
            <p>
              {cycle.status === "charged" && "This week's order has already been charged."}
              {cycle.status === "skipped" && "You already skipped this week's order."}
              {cycle.status === "canceled" && "This week's order was canceled."}
            </p>

            {submitError && <div className="gg-error">{submitError}</div>}

            {cycle.status === "skipped" && cycle.canReactivate && (
              <div className="gg-card">
                <p>Changed your mind? It's still before Saturday 10pm — you can bring this week's order back.</p>
                <button className="gg-btn" disabled={submitting} onClick={handleReactivate}>
                  {submitting ? "Working…" : "Change My Mind"}
                </button>
              </div>
            )}
          </>
        )}

        {!loading && !loadError && cycle && cycle.status === "pending" && !result && (
          <>
            <h1>This Week's Order</h1>
            <p>Pickup {cycle.weekOfLabel}. Add anything extra below.</p>

            {cycle.declineReason && (
              <div className="gg-error">
                We tried to charge your card and it was declined: "{cycle.declineReason}". Please retry below, or update your card via your account page.
              </div>
            )}

            <div className="gg-card">
              <h2>Your Base Order</h2>
              {cycle.baseItems.map((item, i) => (
                <div key={i} className="gg-item-row">
                  <span>{item.name} × {item.quantity}</span>
                  <span>{fmt(item.price * item.quantity)}</span>
                </div>
              ))}
            </div>

            <div className="gg-card">
              <h2>Add Extras (optional)</h2>
              {products.length === 0 && <p style={{ opacity: 0.5, fontSize: 13 }}>No add-ons available right now.</p>}
              {products.map((p) => {
                const qty = extrasQty[String(p.id)] || 0;
                return (
                  <div key={p.id} className="gg-item-row">
                    <span>{p.name} — {fmt(p.price)}</span>
                    <div className="gg-qty-control">
                      <button className="gg-qty-btn" onClick={() => setQty(p.id, qty - 1)} disabled={qty === 0}>−</button>
                      <span style={{ minWidth: 18, textAlign: "center" }}>{qty}</span>
                      <button className="gg-qty-btn" onClick={() => setQty(p.id, qty + 1)}>+</button>
                    </div>
                  </div>
                );
              })}
            </div>

            {cycle.alwaysCoversFee ? (
              <p style={{ fontSize: 12, opacity: 0.6 }}>The card processing fee is included automatically, as you set up at signup.</p>
            ) : (
              <div className="gg-card" style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <input
                  type="checkbox"
                  id="coverFee"
                  checked={coverFee}
                  onChange={(e) => setCoverFee(e.target.checked)}
                  style={{ marginTop: 2, accentColor: "#8C9E82", width: 16, height: 16, flexShrink: 0, cursor: "pointer" }}
                />
                <label htmlFor="coverFee" style={{ fontSize: 13, fontWeight: 300, color: "#1E1C18", lineHeight: 1.6, cursor: "pointer" }}>
                  Cover the card processing fee this week ({fmt(processingFee)}) — helps us keep prices where they are.
                </label>
              </div>
            )}

            <div className="gg-card">
              <div className="gg-item-row" style={{ fontWeight: 500 }}>
                <span>Total</span>
                <span>{fmt(chargeTotal)}</span>
              </div>
            </div>

            {submitError && <div className="gg-error">{submitError}</div>}

            <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
              <button className="gg-btn" disabled={submitting} onClick={handleConfirm} style={{ flex: 1 }}>
                {submitting ? "Processing…" : `Confirm & Charge ${fmt(chargeTotal)}`}
              </button>
              <button className="gg-btn-outline" disabled={submitting} onClick={handleSkip}>Skip This Week</button>
            </div>
            <p style={{ fontSize: 12, opacity: 0.6, marginTop: 10 }}>
              Need to skip this week? Just hit skip before Saturday at 10pm — no charge, and you're back on for next week automatically.
            </p>
          </>
        )}

        {result?.type === "confirmed" && (
          <>
            <h1>Order Confirmed</h1>
            <div className="gg-success">Your card has been charged and your order is confirmed. A confirmation email is on its way.</div>
          </>
        )}

        {result?.type === "skipped" && (
          <>
            <h1>Week Skipped</h1>
            <div className="gg-success">This week's order has been skipped — you won't be charged. We'll see you next week.</div>
          </>
        )}
      </div>
    </div>
  );
}
